import React, { useEffect, useState } from 'react';
import Sidebar from './Components/Sidebar/Sidebar';
import '../css/Dash.css';
import { collection, getDocs } from 'firebase/firestore';
import db from '../firebase-config';
import { BsCashCoin, BsGraphUpArrow, BsClock, BsPeopleFill } from 'react-icons/bs';

function Dashboard() {
  const [prestamos, setPrestamos] = useState([]);
  const [clientesMap, setClientesMap] = useState({});
  const [totalClientes, setTotalClientes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalPago, setModalPago] = useState(null);
  const [diaSeleccionado, setDiaSeleccionado] = useState(() => new Date());

  useEffect(() => {
    let cancelled = false;
    const obtenerDatos = async () => {
      const [prestamosSnap, clientesSnap] = await Promise.all([
        getDocs(collection(db, 'prestamos')),
        getDocs(collection(db, 'clientes')),
      ]);
      if (cancelled) return;

      const list = [];
      prestamosSnap.forEach(doc => list.push({ id: doc.id, ...doc.data() }));

      const mapa = {};
      clientesSnap.forEach(doc => {
        const d = doc.data();
        if (d.nombre) mapa[d.nombre] = d.telefono || '';
      });

      setPrestamos(list);
      setClientesMap(mapa);
      setTotalClientes(clientesSnap.size);
      setLoading(false);
    };
    obtenerDatos();
    return () => { cancelled = true; };
  }, []);

  const hoy = new Date();
  const startOfDay = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  // En pagos mensuales el DÍA es constante entre fechas.
  // Detecta el formato comparando cuál parte (primera o segunda) no cambia.
  const detectarFormato = (pagos) => {
    if (!Array.isArray(pagos) || pagos.length === 0) return 'md';
    const partes = pagos.map(p => p.split('/').map(Number)).filter(p => p.length === 3);
    if (partes.length === 0) return 'md';
    // Si algún primer elemento > 12, es imposible que sea el mes → formato D/M
    if (partes.some(([a]) => a > 12)) return 'dm';
    // Si algún segundo elemento > 12, es imposible que sea el mes → formato M/D
    if (partes.some(([, b]) => b > 12)) return 'md';
    // Ambiguo: usar consistencia del día entre fechas
    const diasMD = partes.map(([a, b]) => b);
    const diasDM = partes.map(([a, b]) => a);
    if (diasMD.every(d => d === diasMD[0])) return 'md';
    if (diasDM.every(d => d === diasDM[0])) return 'dm';
    return 'md';
  };

  const parseFechaConFormato = (str, fmt) => {
    const [a, b, year] = str.split('/').map(Number);
    return fmt === 'dm' ? new Date(year, b - 1, a) : new Date(year, a - 1, b);
  };

  const unAnio = new Date(startOfDay);
  unAnio.setFullYear(unAnio.getFullYear() + 1);

  // Pagos con fecha >= hoy y <= 1 año (evita fechas erróneas lejanas)
  const getPagosVigentes = (p) => {
    if (!Array.isArray(p.Pagos)) return [];
    const fmt = detectarFormato(p.Pagos);
    return p.Pagos
      .map((f, idx) => ({ fecha: parseFechaConFormato(f, fmt), idx }))
      .filter(({ fecha }) => !isNaN(fecha) && fecha >= startOfDay && fecha <= unAnio);
  };

  // Activo = tiene al menos 1 pago futuro, sin importar el campo activo de Firestore
  const activos = prestamos.filter(p => getPagosVigentes(p).length > 0);
  const parseAmt = v => Number(String(v).replace(/[^0-9.]/g, '')) || 0;
  const capitalTotal = activos.reduce((sum, p) => sum + parseAmt(p.cantidadPrestada), 0);
  const interesesTotal = capitalTotal * 0.03;

  const fmt = n => parseAmt(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

  // ── Calendario semanal (hoy + 6 días) ────────────────────
  const semana = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(hoy);
    d.setDate(hoy.getDate() - 1 + i);
    return d;
  });

  const pagosEnDia = (dia) =>
    activos.filter(p =>
      getPagosVigentes(p).some(({ fecha }) => fecha.toDateString() === dia.toDateString())
    );

  const nombreDia = (d) => d.toLocaleDateString('es-MX', { weekday: 'short' });
  const nombreMes  = (d) => d.toLocaleDateString('es-MX', { month: 'short' });
  const esHoy = (d) => d.toDateString() === hoy.toDateString();
  const esSeleccionado = (d) => d.toDateString() === diaSeleccionado.toDateString();

  // ── Pagos del día seleccionado ────────────────────────────
  const pagosDia = activos
    .filter(p => getPagosVigentes(p).some(({ fecha }) => fecha.toDateString() === diaSeleccionado.toDateString()))
    .map(p => {
      const match = getPagosVigentes(p).find(({ fecha }) => fecha.toDateString() === diaSeleccionado.toDateString());
      return {
        deudor: p.deudor,
        telefono: clientesMap[p.deudor] || '',
        cantidad: p.cantidadinteres,
        pagado: p.PagosStatus?.[match?.idx] ?? false,
      };
    });

  const tituloDia = esHoy(diaSeleccionado)
    ? 'Cobros de hoy'
    : `Cobros del ${diaSeleccionado.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}`;

  return (
    <div className="wrapper">
      <nav><Sidebar /></nav>
      <div className="marg">

        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p>{hoy.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        {/* ── KPI Cards ── */}
        <div className="dashboard-cards">
          <div className="dash-card">
            <div className="dash-card-icon dash-icon-blue"><BsCashCoin size={20} /></div>
            <div>
              <span className="dash-card-label">Préstamos vigentes</span>
              <span className="dash-card-value">{loading ? '—' : activos.length}</span>
            </div>
          </div>
          <div className="dash-card">
            <div className="dash-card-icon dash-icon-green"><BsGraphUpArrow size={20} /></div>
            <div>
              <span className="dash-card-label">Capital prestado</span>
              <span className="dash-card-value dash-card-value--sm">{loading ? '—' : fmt(capitalTotal)}</span>
            </div>
          </div>
          <div className="dash-card">
            <div className="dash-card-icon dash-icon-red"><BsPeopleFill size={20} /></div>
            <div>
              <span className="dash-card-label">Clientes</span>
              <span className="dash-card-value">{loading ? '—' : totalClientes}</span>
            </div>
          </div>
          <div className="dash-card">
            <div className="dash-card-icon dash-icon-amber"><BsClock size={20} /></div>
            <div>
              <span className="dash-card-label">Intereses totales</span>
              <span className="dash-card-value dash-card-value--sm">{loading ? '—' : fmt(interesesTotal)}</span>
            </div>
          </div>
        </div>

        {/* ── Sección inferior ── */}
        <div className="db-section-grid">

          {/* Calendario semanal */}
          <div className="db-widget">
            <div className="db-widget-header">Selecciona un día</div>
            {loading ? (
              <p className="db-empty">Cargando...</p>
            ) : (
              <div className="week-cal">
                {semana.map((dia, i) => {
                  const pagos = pagosEnDia(dia);
                  return (
                    <div
                      key={i}
                      onClick={() => setDiaSeleccionado(dia)}
                      className={[
                        'week-day',
                        esHoy(dia) ? 'week-day--hoy' : '',
                        esSeleccionado(dia) ? 'week-day--sel' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="week-day-nombre">{nombreDia(dia)}</div>
                      <div className="week-day-num">{dia.getDate()}</div>
                      <div className="week-day-mes">{nombreMes(dia)}</div>
                      <div className="week-day-pagos">
                        {pagos.length === 0
                          ? <span className="week-day-empty">—</span>
                          : <span className="week-payer">{pagos.length}</span>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cobros del día seleccionado */}
          <div className="db-widget">
            <div className="db-widget-header">
              {tituloDia}
              <span className="db-widget-badge">{pagosDia.length}</span>
            </div>
            {loading ? (
              <p className="db-empty">Cargando...</p>
            ) : pagosDia.length === 0 ? (
              <p className="db-empty">Sin cobros este día</p>
            ) : (
              <div className="mes-pagos-list">
                {pagosDia.map((p, i) => (
                  <div key={i} className={`mes-pago-row${p.pagado ? ' pagado' : ''}`} onClick={() => setModalPago(p)} style={{ cursor: 'pointer' }}>
                    <div className="mes-pago-deudor">{p.deudor}</div>
                    <div className="mes-pago-cantidad">{fmt(p.cantidad)}</div>
                    <div className={`mes-pago-status ${p.pagado ? 'status-ok' : 'status-pend'}`}>
                      {p.pagado ? '✓ Pagado' : 'Pendiente'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Modal detalle de pago ── */}
      {modalPago && (
        <>
          <div
            onClick={() => setModalPago(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001, background: '#fff', borderRadius: '12px',
            width: '90%', maxWidth: '360px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            fontFamily: 'Poppins, sans-serif',
          }}>
            <div style={{ padding: '1.1rem 1.4rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1a202c' }}>Detalle de pago</span>
              <button onClick={() => setModalPago(null)} style={{ border: 'none', background: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#718096', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1.25rem 1.4rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>Cliente</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1a202c' }}>{modalPago.deudor}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>Teléfono</div>
                {modalPago.telefono
                  ? <a href={`tel:${modalPago.telefono}`} style={{ fontSize: '1.1rem', fontWeight: 600, color: '#2b6cb0', textDecoration: 'none' }}>{modalPago.telefono}</a>
                  : <span style={{ fontSize: '0.875rem', color: '#a0aec0' }}>Sin teléfono registrado</span>
                }
              </div>
              <div style={{ display: 'flex', gap: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>Fecha</div>
                  <div style={{ fontSize: '0.9rem', color: '#2d3748' }}>{diaSeleccionado.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.3rem' }}>Cantidad</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2b6cb0' }}>{fmt(modalPago.cantidad)}</div>
                </div>
              </div>
              <div>
                <span style={{
                  fontSize: '0.75rem', fontWeight: 600, padding: '0.25rem 0.7rem', borderRadius: '999px',
                  ...(modalPago.pagado ? { background: '#f0fff4', color: '#276749' } : { background: '#fffbeb', color: '#92400e' })
                }}>
                  {modalPago.pagado ? '✓ Pagado' : 'Pendiente'}
                </span>
              </div>
            </div>
            {modalPago.telefono && (
              <div style={{ padding: '0.9rem 1.4rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                <a href={`tel:${modalPago.telefono}`} style={{
                  padding: '0.55rem 1.2rem',
                  background: 'linear-gradient(135deg, #2b6cb0, #2c5282)',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  fontSize: '0.875rem', fontWeight: 500,
                  fontFamily: 'Poppins, sans-serif', textDecoration: 'none',
                  cursor: 'pointer',
                }}>
                  Llamar
                </a>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
